import { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faUser, faVideo, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import styles from "../WorkoutAdminModal.module.css";
import Badge from "./Badge";

export default function MyVideosTab({
  selectedCategoryId,
  setSelectedCategoryId,
  closeProfessorMediaEditor,
  categories,
  selectedCategory,
  categoryItems,
  filteredCategoryItems,
  exerciseItemSearch,
  setExerciseItemSearch,
  startCreateExerciseItem,
  currentUser,
  professorMediaByItemId,
  editingProfessorMediaItemId,
  professorMediaUrlInput,
  setProfessorMediaUrlInput,
  savingProfessorMedia,
  handleSaveProfessorDemoUrl,
  handleUploadProfessorDemo,
  setDemoExerciseItem,
  openProfessorMediaEditor,
  handleRemoveProfessorDemo,
  startEditExerciseItem,
  handleDeleteExerciseItem,
  saving,
}) {
  // Exercícios criados pelo professor primeiro, depois os da plataforma.
  // Sort estável: dentro de cada grupo mantém a ordem alfabética da API.
  const orderedItems = useMemo(
    () => [...filteredCategoryItems].sort(
      (a, b) => (a.professor_id === currentUser?.id ? 0 : 1) - (b.professor_id === currentUser?.id ? 0 : 1)
    ),
    [filteredCategoryItems, currentUser?.id]
  );
  return (
    <div className={`${styles.wamGrid} ${selectedCategoryId ? styles.hasSelection : ""}`}>
      <div className={styles.wamSidebar}>
        <div className={styles.sectionHead}>
          <div className={styles.sectionHeadLeft}>
            <div className={styles.titleLine}>
              <h3>Categorias</h3>
              <Badge tone="info">{categories.length}</Badge>
            </div>
          </div>
        </div>

        <div className={styles.wamList}>
          {categories.map((category) => (
            <button
              key={category.id}
              type="button"
              className={`${styles.wamListItem} ${
                selectedCategoryId === category.id ? styles.activeItem : ""
              }`}
              aria-pressed={selectedCategoryId === category.id}
              onClick={() => {
                setSelectedCategoryId(category.id);
                closeProfessorMediaEditor();
              }}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.wamMain}>
        {selectedCategory && (
          <div className={styles.wamMobileBackBar}>
            <button
              type="button"
              className={styles.wamMobileBackIcon}
              aria-label="Voltar para categorias"
              onClick={() => setSelectedCategoryId(null)}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <strong>{selectedCategory.name}</strong>
            <button
              type="button"
              className={styles.wamMobileSwitchBtn}
              onClick={() => setSelectedCategoryId(null)}
            >
              Trocar
            </button>
          </div>
        )}
        <div className={styles.sectionHead}>
          <div className={styles.sectionHeadLeft}>
            <h3>{selectedCategory?.name || "Categoria"}</h3>
            {selectedCategory && <Badge>{selectedCategory.name}</Badge>}
          </div>
        </div>

        <div className={styles.cardGlass}>
          <div className={styles.sectionHead}>
            <div className={styles.sectionHeadLeft}>
              <h4>Meus vídeos e exercícios</h4>
              <p className={styles.sectionHint}>
                Defina seu próprio vídeo pra um exercício existente, ou crie exercícios
                exclusivos seus - eles passam a valer só pra você e seus alunos, sem
                aparecer pra outros professores. Sem nada definido aqui, seus alunos
                continuam vendo a demonstração padrão da plataforma.
              </p>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {selectedCategoryId && categoryItems.length > 0 && (
                <input
                  type="search"
                  className={styles.searchInput}
                  placeholder="Buscar exercício..."
                  value={exerciseItemSearch}
                  onChange={(e) => setExerciseItemSearch(e.target.value)}
                  aria-label="Buscar exercício na categoria"
                />
              )}
              <Badge tone="soft">
                {exerciseItemSearch
                  ? `${filteredCategoryItems.length}/${categoryItems.length}`
                  : categoryItems.length}
              </Badge>
              {selectedCategoryId && (
                <button
                  type="button"
                  className={`${styles.wamBtn} ${styles.wamBtnSm} ${styles.primary}`}
                  onClick={startCreateExerciseItem}
                >
                  + Criar exercício
                </button>
              )}
            </div>
          </div>

          <div className={styles.simpleList}>
            {orderedItems.map((item) => {
              const isOwn = item.professor_id === currentUser?.id;
              // Exercício próprio do professor não passa pela camada de
              // override (ProfessorExerciseMedia) — o demo_url já é
              // editável direto nele (ver "Editar exercício"). O
              // conceito "padrão da plataforma vs meu vídeo" só existe
              // pros exercícios GLOBAIS (professor_id nulo).
              const mine = isOwn ? null : professorMediaByItemId[item.id];
              const editing = !isOwn && editingProfessorMediaItemId === item.id;
              return (
                <div className={styles.simpleRow} key={item.id}>
                  <div className={styles.simpleRowMain}>
                    <div className={styles.simpleRowTop}>
                      <strong>{item.name}</strong>
                      {isOwn && (
                        <span className={styles.customDemoBadge}>
                          <FontAwesomeIcon icon={faUser} /> Seu exercício
                        </span>
                      )}
                      {isOwn && (!item.muscle_group || !item.equipment) && (
                        // Sem grupo/equipamento a IA não consegue priorizar o
                        // exercício (ver ai_prompt_service.covered_muscle_groups).
                        <button
                          type="button"
                          className={styles.customDemoBadge}
                          style={{ background: "rgba(245,158,11,0.14)", color: "var(--feedback-warning)", border: 0, cursor: "pointer", font: "inherit" }}
                          onClick={() => startEditExerciseItem(item)}
                          title="A geração de treino por IA precisa do grupo muscular e do equipamento pra usar este exercício"
                        >
                          <FontAwesomeIcon icon={faTriangleExclamation} />{" "}
                          {!item.muscle_group && !item.equipment
                            ? "Falta grupo muscular e equipamento"
                            : !item.muscle_group ? "Falta grupo muscular" : "Falta equipamento"}
                        </button>
                      )}
                      {!isOwn && (
                        mine ? (
                          <span className={styles.customDemoBadge}>
                            <FontAwesomeIcon icon={faVideo} /> Vídeo próprio definido
                          </span>
                        ) : (
                          <Badge tone="muted">Usando padrão da plataforma</Badge>
                        )
                      )}
                      {!isOwn && mine?.demo_status === "failed" && (
                        // Vídeo continua tocável (fallback pro arquivo bruto, ver
                        // ExerciseDemoModal.jsx) — isto é um aviso, não um erro
                        // bloqueante, por isso o tom âmbar/warning, não vermelho.
                        <span
                          className={styles.customDemoBadge}
                          style={{ background: "rgba(245,158,11,0.14)", color: "var(--feedback-warning)" }}
                          title="O vídeo enviado não pôde ser otimizado automaticamente. Ele continua funcionando, mas considere reenviar um arquivo menor."
                        >
                          <FontAwesomeIcon icon={faTriangleExclamation} /> Otimização falhou
                        </span>
                      )}
                    </div>

                    {editing && (
                      <div className={styles.wamField} style={{ marginTop: 8 }}>
                        <label className={styles.wamFieldSubLabel}>
                          URL da demonstração (YouTube/Shorts, GIF/MP4/WEBM ou imagem)
                        </label>
                        <div className={styles.wamFieldRow}>
                          <input
                            type="text"
                            placeholder="Ex.: https://youtube.com/shorts/ID ou https://..."
                            value={professorMediaUrlInput}
                            onChange={(e) => setProfessorMediaUrlInput(e.target.value)}
                            disabled={savingProfessorMedia}
                            style={{ flex: 1, minWidth: 0 }}
                          />
                          <button
                            type="button"
                            className={`${styles.wamBtn} ${styles.smallGhost}`}
                            onClick={() => handleSaveProfessorDemoUrl(item.id)}
                            disabled={savingProfessorMedia || !professorMediaUrlInput.trim()}
                          >
                            Salvar link
                          </button>
                        </div>

                        <label className={styles.wamFieldSubLabel}>
                          Ou envie um arquivo (vídeo/gif/imagem) - a ação mais recente vale
                        </label>
                        <input
                          type="file"
                          accept="video/mp4,video/webm,video/quicktime,.mov,.gif,.png,.jpg,.jpeg,.webp"
                          disabled={savingProfessorMedia}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleUploadProfessorDemo(item.id, file);
                            e.target.value = "";
                          }}
                        />

                        <div className={styles.wamFieldRow} style={{ marginTop: 8 }}>
                          <button
                            type="button"
                            className={`${styles.wamBtn} ${styles.ghost}`}
                            onClick={closeProfessorMediaEditor}
                            disabled={savingProfessorMedia}
                          >
                            Cancelar
                          </button>
                        </div>
                        {savingProfessorMedia && <small>Salvando…</small>}
                      </div>
                    )}
                  </div>

                  <div className={styles.simpleRowActions}>
                    {isOwn && item.demo_url && (
                      <button
                        type="button"
                        className={`${styles.wamBtn} ${styles.ghost}`}
                        onClick={() => setDemoExerciseItem(item)}
                      >
                        ▶ Ver vídeo
                      </button>
                    )}
                    {!isOwn && mine && (
                      <button
                        type="button"
                        className={`${styles.wamBtn} ${styles.ghost}`}
                        onClick={() => setDemoExerciseItem({ ...item, demo_url: mine.demo_url })}
                      >
                        ▶ Meu vídeo
                      </button>
                    )}
                    {!isOwn && !mine && item.demo_url && (
                      <button
                        type="button"
                        className={`${styles.wamBtn} ${styles.ghost}`}
                        onClick={() => setDemoExerciseItem(item)}
                      >
                        ▶ Padrão
                      </button>
                    )}
                    {!isOwn && !editing && (
                      <button
                        type="button"
                        className={`${styles.wamBtn} ${styles.ghost}`}
                        onClick={() => openProfessorMediaEditor(item)}
                      >
                        {mine ? "Editar" : "Definir vídeo"}
                      </button>
                    )}
                    {!isOwn && mine && (
                      <button
                        type="button"
                        className={`${styles.wamBtn} ${styles.danger}`}
                        onClick={() => handleRemoveProfessorDemo(item.id)}
                        disabled={savingProfessorMedia}
                      >
                        Remover
                      </button>
                    )}
                    {isOwn && (
                      <>
                        <button
                          type="button"
                          className={`${styles.wamBtn} ${styles.ghost}`}
                          onClick={() => startEditExerciseItem(item)}
                        >
                          Editar exercício
                        </button>
                        <button
                          type="button"
                          className={`${styles.wamBtn} ${styles.danger}`}
                          onClick={() => handleDeleteExerciseItem(item.id)}
                          disabled={saving}
                        >
                          Excluir
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}

            {!categoryItems.length && (
              <div className={styles.wamEmpty}>Nenhum exercício cadastrado nesta categoria.</div>
            )}
            {categoryItems.length > 0 && !filteredCategoryItems.length && (
              <div className={styles.wamEmpty}>
                Nenhum exercício encontrado para "{exerciseItemSearch}".
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
